import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import ChatIcon from '@mui/icons-material/Chat';
import { styled } from '@mui/material/styles';

// Styled components for premium look
const GradientCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2c3e50 0%, #4a6491 100%)',
  color: 'white',
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #ff6b6b 0%, #ff8e53 100%)',
  }
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)',
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '60px',
  height: '60px',
  borderRadius: '12px',
  marginBottom: '16px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
}));

const ReportCard = ({ title, value, description, icon, trend, color }) => (
  <StatCard>
    <CardContent sx={{ p: 3 }}>
      <IconWrapper>
        {icon}
      </IconWrapper>
      <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
        {value}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#2c3e50' }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TrendingUpIcon sx={{ fontSize: 18, color: trend >= 0 ? '#4caf50' : '#f44336', mr: 0.5 }} />
        <Typography variant="body2" sx={{ color: trend >= 0 ? '#4caf50' : '#f44336', fontWeight: 600 }}>
          {trend >= 0 ? '+' : ''}{trend}% from last week
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
        {description}
      </Typography>
    </CardContent>
  </StatCard>
);

const HomeDas = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const reportsData = [
    { 
      title: 'Total Applications', 
      value: '1,248', 
      description: 'Loan applications received this month', 
      icon: <RequestPageIcon sx={{ fontSize: 28 }} />,
      trend: 12.5
    },
    { 
      title: 'Refund Requests', 
      value: '327', 
      description: 'Refund requests being processed', 
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      trend: -3.2
    },
    { 
      title: 'Contact Messages', 
      value: '842', 
      description: 'Customer inquiries this month', 
      icon: <ChatIcon sx={{ fontSize: 28 }} />,
      trend: 8.7
    },
    { 
      title: 'Pending Approvals', 
      value: '184', 
      description: 'Applications awaiting approval', 
      icon: <RequestPageIcon sx={{ fontSize: 28 }} />,
      trend: 5.3
    },
  ];

  const doughnutData = {
    labels: ['Approved', 'Pending', 'Rejected', 'Under Review'],
    datasets: [
      {
        data: [45, 25, 15, 15],
        backgroundColor: ['#4caf50', '#ff9800', '#f44336', '#2196f3'],
        borderWidth: 0,
        borderRadius: 5,
        hoverOffset: 10
      },
    ],
  };

  const doughnutOptions = {
    maintainAspectRatio: false,
    responsive: true,
    cutout: '70%',
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12,
            weight: 600
          }
        }
      },
    },
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
  };

  const barChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Applications',
        data: [65, 78, 90, 81, 96, 110],
        backgroundColor: 'rgba(76, 175, 80, 0.7)',
        borderColor: '#4caf50',
        borderWidth: 2,
        borderRadius: 5,
        barPercentage: 0.6,
      },
      {
        label: 'Approvals',
        data: [50, 62, 75, 65, 80, 92],
        backgroundColor: 'rgba(33, 150, 243, 0.7)',
        borderColor: '#2196f3',
        borderWidth: 2,
        borderRadius: 5,
        barPercentage: 0.6,
      }
    ],
  };

  const barChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            weight: 600
          }
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          drawBorder: false,
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  const lineChartData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [
      {
        label: 'Approval Rate',
        data: [65, 72, 78, 85],
        borderColor: '#ff6b6b',
        backgroundColor: 'rgba(255, 107, 107, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#ff6b6b',
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ],
  };

  const lineChartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        min: 60,
        max: 90,
        grid: {
          drawBorder: false,
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: '#2c3e50' }}>
        Dashboard Overview
      </Typography>

      <Grid container spacing={3}>
        {reportsData.map((report, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <ReportCard
              title={report.title}
              value={report.value}
              description={report.description}
              icon={report.icon}
              trend={report.trend}
            />
          </Grid>
        ))}

        <Grid item xs={12} lg={8}>
          <GradientCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'white', fontWeight: 600 }}>
                Monthly Applications & Approvals
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar data={barChartData} options={barChartOptions} />
              </Box>
            </CardContent>
          </GradientCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <StatCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                Application Status
              </Typography>
              <Box sx={{ height: 300 }}>
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </Box>
            </CardContent>
          </StatCard>
        </Grid>

        <Grid item xs={12}>
          <StatCard>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                Weekly Approval Rate Trend
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line data={lineChartData} options={lineChartOptions} />
              </Box>
            </CardContent>
          </StatCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HomeDas;